import React, {useState, useEffect, useCallback} from 'react';
import {Button, Icon, Text, Toggle, addToast, Label, Popover, WithToggle} from '@albert-io/atomic';
import {Map, List} from 'immutable';
import {ClassroomModelV1} from 'resources/augmented/Classroom/ClassroomModel.v1';
import {ClassroomAssignmentSettings} from './RecipientSettings.types';
import useOnClickOutside from 'lib/hooks/useOnClickOutside';
import {getActiveClassroomsQuery} from '../settingsModal.utils';

import './add-classrooms-dropdown.scss';

interface AddClassroomsDropdownProps {
  selectedClassrooms: Map<string, ClassroomAssignmentSettings>;
  setSelectedClassrooms: (selectedClassrooms: Map<string, ClassroomAssignmentSettings>) => void;
  popoverRef: any;
  showClassDropdownPopover: Boolean;
  setShowClassDropdownPopover: (showClassDropdownPopover) => void;
}

type ReduceReturnType = Map<string, ClassroomAssignmentSettings>;

const AddClassroomsDropdown = ({
  selectedClassrooms,
  setSelectedClassrooms,
  popoverRef,
  showClassDropdownPopover,
  setShowClassDropdownPopover
}: AddClassroomsDropdownProps) => {
  const [activeClassrooms, setActiveClassrooms] = useState<List<ClassroomModelV1>>(List());
  const [buttonSelectText, setButtonSelectText] = useState('Select All');

  useOnClickOutside(
    popoverRef,
    useCallback(
      () => !!showClassDropdownPopover && setShowClassDropdownPopover(false),
      [showClassDropdownPopover, setShowClassDropdownPopover]
    )
  );

  useEffect(() => {
    let subscribed = true;
    const fetchClassrooms = async () => {
      try {
        const classroomsList = await getActiveClassroomsQuery().getResourcePromise();
        if (subscribed) {
          setActiveClassrooms(classroomsList);
        }
      } catch (err) {
        addToast({
          type: 'danger',
          title: 'Error',
          message: 'Something went wrong with your request. Please try again.'
        });
      }
    };
    fetchClassrooms();
    return () => {
      subscribed = false;
    };
  }, []);

  const toggleClassroom = (classroom) => {
    let updatedClassrooms = selectedClassrooms;
    if (!selectedClassrooms.has(classroom.getId())) {
      updatedClassrooms = selectedClassrooms.set(classroom.getId(), {
        classroomInfo: classroom,
        selectedStudents: classroom.getStudents(),
        autoAssignNewEnrollees: true,
        startDate: '',
        startDateTime: '06:00:00',
        dueDate: '',
        dueDateTime: '23:59:00'
      });
    } else {
      updatedClassrooms = selectedClassrooms.delete(classroom.getId());
    }
    setSelectedClassrooms(updatedClassrooms);
  };

  return (
    <WithToggle className='u-width_fit-content'>
      {({on, onClick}) => (
        <span ref={popoverRef} style={{display: 'inline-block'}}>
          <Button
            data-test-id='AB-settings-modal__classroom-dropdown'
            size='m'
            variant='faded'
            onClick={() => {
              onClick();
              setShowClassDropdownPopover(!showClassDropdownPopover);
            }}
          >
            Choose Classrooms...
            <Icon icon='caret-down' className='u-mar-l_2' />
          </Button>
          {on && (
            <Popover
              optimizeForMobile
              expanded={on}
              position='bottom-start'
              targetRef={popoverRef}
              paddingLevel={0}
              modifiers={{
                preventOverflow: {
                  boundariesElement: 'window'
                }
              }}
              usePortal={false}
            >
              <div className='add-classrooms-dropdown__class-list'>
                {activeClassrooms.size > 0 &&
                  activeClassrooms.map((classroom) => {
                    const isChecked = selectedClassrooms.has(classroom.getId());
                    return (
                      <Label className='add-classrooms-dropdown__class-item'>
                        <Text size='m'>{classroom.getName()}</Text>
                        <Toggle
                          className='u-mar-l_2'
                          onChange={() => toggleClassroom(classroom)}
                          checked={isChecked}
                        />
                      </Label>
                    );
                  })}
              </div>
              <div className='u-display_flex u-justify-content_space-between u-border-t u-border-color_slate-300 u-pad_1'>
                <Button
                  color='secondary'
                  variant='text'
                  size='s'
                  onClick={() => {
                    // checking whether all items are already checked
                    if (selectedClassrooms.size !== activeClassrooms.size) {
                      const updatedClassrooms = activeClassrooms.reduce<ReduceReturnType>(
                        (classroomMap, classroom) => {
                          return classroomMap!.set(classroom.getId(), {
                            classroomInfo: classroom,
                            selectedStudents: classroom.getStudents(),
                            autoAssignNewEnrollees: true,
                            startDate: '',
                            startDateTime: '06:00:00',
                            dueDate: '',
                            dueDateTime: '23:59:00'
                          });
                        },
                        Map<string, ClassroomAssignmentSettings>()
                      );
                      setSelectedClassrooms(updatedClassrooms);
                      setButtonSelectText('Deselect All');
                    } else {
                      // case for when all items are unchecked
                      setSelectedClassrooms(Map());
                      setButtonSelectText('Select All');
                    }
                  }}
                  className='u-mar-r_1'
                >
                  {buttonSelectText}
                </Button>
                <Button
                  data-test-id='AB-settings-modal__classroom-done-btn'
                  size='s'
                  variant='text'
                  onClick={onClick}
                >
                  Done
                </Button>
              </div>
            </Popover>
          )}
        </span>
      )}
    </WithToggle>
  );
};

export default AddClassroomsDropdown;
